import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { JobPhase } from './JobPhaseSelector';

/** Matches JobFileCaptureBar — the mobile pass's minimum tap target. */
const TOUCH_TARGET = 'min-h-[44px]';

interface SortOption {
  field: string;
  label: string;
}

/**
 * Deliberately a subset of the desktop table's sortable columns. A phone shows a
 * card, not thirteen columns, so offering every column would be offering sorts
 * whose effect the user cannot see.
 */
const BASE_OPTIONS: SortOption[] = [
  { field: 'statusDate', label: 'Date' },
  { field: 'status', label: 'Job Status' },
  { field: 'jobCode', label: 'Job Code' },
  { field: 'client', label: 'Client' },
  { field: 'location', label: 'Service Address' },
  { field: 'projectType', label: 'Project Type' },
];

/** The same phase-conditional columns JobListTable shows, so the two stay in step. */
const PHASE_OPTIONS: Record<JobPhase, SortOption[]> = {
  all: [{ field: 'description', label: 'Description' }],
  other: [{ field: 'description', label: 'Description' }],
  quoting: [{ field: 'noticeDate', label: 'Notice Date' }],
  building: [{ field: 'startDate', label: 'Start Date' }],
  billing: [{ field: 'completedDate', label: 'Completed Date' }],
};

interface JobListSortMenuProps {
  selectedPhase: JobPhase;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  /** The same handler the table headers use: the same field again flips direction. */
  onSort: (field: string) => void;
  className?: string;
}

export function JobListSortMenu({
  selectedPhase,
  sortField,
  sortDirection,
  onSort,
  className = '',
}: JobListSortMenuProps) {
  const options = [...BASE_OPTIONS, ...PHASE_OPTIONS[selectedPhase]];
  const active = options.find((o) => o.field === sortField);
  const DirectionIcon = sortDirection === 'desc' ? ArrowDown : ArrowUp;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={`${TOUCH_TARGET} gap-1.5 px-3 ${className}`}
          aria-label={active ? `Sort: ${active.label}` : 'Sort'}
        >
          <ArrowUpDown className="h-4 w-4" />
          <span className="truncate">{active ? active.label : 'Sort'}</span>
          {active && <DirectionIcon className="h-3.5 w-3.5 text-gray-500" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        {options.map((option) => {
          const isActive = option.field === sortField;
          return (
            <DropdownMenuItem
              key={option.field}
              onClick={() => onSort(option.field)}
              className={`${TOUCH_TARGET} justify-between ${isActive ? 'font-semibold' : ''}`}
            >
              <span>{option.label}</span>
              {isActive && <DirectionIcon className="h-4 w-4 text-gray-500" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
